import { getContractManagementContractFile, type ContractManagementContractFileInDb } from '@/api';
import { useAuthStore } from '@/stores/auth';
import { logger } from '@/utils/logger';
import { ApiError } from '@/utils/errorHandler';

/**
 * Service for downloading contract files from the vault.
 * Follows the same pattern as vaultStore.downloadFile for consistency.
 */
export const ContractFileDownloadService = {
  /**
   * Extract vault file identifier from contract file.
   * Uses the vault_file relationship from the API response.
   *
   * @param contractFile - The contract file object with vault_file relationship
   * @returns The vault file UID if available, null otherwise
   */
  extractVaultFileIdentifier(contractFile: ContractManagementContractFileInDb): string | null {
    return contractFile.vault_file?.current_revision?.uid ?? null;
  },

  /**
   * Download a contract file by fetching the contract file details first,
   * then downloading the associated vault file.
   *
   * @param contractFileUid - The UID of the contract file to download
   * @param fileName - Optional file name for the download (falls back to description or UID)
   * @returns Promise that resolves when download is complete
   */
  async downloadContractFile(
    contractFileUid: string,
    fileName?: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // First, fetch the contract file to get the vault file information
      const contractFileResponse = await getContractManagementContractFile({
        path: { uid: contractFileUid },
      });

      if (!contractFileResponse.data) {
        throw new Error('Failed to fetch contract file details');
      }

      const contractFile: ContractManagementContractFileInDb = contractFileResponse.data;

      // Extract vault file identifier
      const vaultFileIdentifier = this.extractVaultFileIdentifier(contractFile);

      if (!vaultFileIdentifier) {
        return {
          success: false,
          error:
            'File download is not available for this contract file. The contract file does not have an associated vault file.',
        };
      }

      // Get content type from vault_file if available, otherwise use default
      const contentType =
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        contractFile.vault_file?.current_revision?.mime_type || 'application/octet-stream';

      // Download the file from vault using the same pattern as vaultStore
      const blob = await this.downloadVaultFile(vaultFileIdentifier, contentType);

      // Determine file name (prefer vault_file.file_name, then provided fileName, then description, then fallback)
      // vault_file.file_name is the most accurate as it comes from the actual file
      const downloadFileName =
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
        contractFile.vault_file?.current_revision?.file_name ||
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
        fileName ||
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        contractFile.description ||
        `contract-file-${contractFileUid.slice(0, 8)}`;

      // Trigger download
      this.triggerDownload(blob, downloadFileName);

      return { success: true };
    } catch (error) {
      logger.error('Error downloading contract file:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to download file';
      return { success: false, error: errorMessage };
    }
  },

  /**
   * Download a file from the vault by its identifier (UID or ID).
   * This follows the same pattern as vaultStore.downloadFile.
   *
   * @param fileIdentifier - The UID or ID of the vault file
   * @param contentType - Optional content type (defaults to application/octet-stream)
   * @returns Promise that resolves with the file Blob
   */
  async downloadVaultFile(
    fileIdentifier: string,
    contentType: string = 'application/octet-stream'
  ): Promise<Blob> {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const url = `${apiUrl}/v1/app/repository/vault/file/revision/${fileIdentifier}`;

    const authStore = useAuthStore();
    const token = await authStore.user?.getIdToken();
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      // A draft that no longer renders answers 422 with a detail.
      throw new ApiError(response.status, await response.json().catch(() => null));
    }

    return new Blob([await response.arrayBuffer()], { type: contentType });
  },

  /**
   * Trigger a file download in the browser.
   * This follows the same pattern as FileVault.vue downloadFile.
   *
   * @param blob - The file blob to download
   * @param fileName - The name for the downloaded file
   */
  triggerDownload(blob: Blob, fileName: string): void {
    // Create a download link
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    // Clean up
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
  },
};
